import  { useState, useEffect } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { motion } from 'framer-motion'
import { Trophy, Users, TrendingUp, Award, Filter, Info } from 'lucide-react'
import { Card, CardContent } from '../components/ui/Card'
import { Badge } from '../components/ui/Badge'
import { Button } from '../components/ui/Button'
import { Skeleton } from '../components/ui/Skeleton'
import Logo from '../components/ui/Logo'
import Breadcrumb from '../components/ui/Breadcrumb'
import ProjectRankingCard from '../components/leaderboards/ProjectRankingCard'
import LeaderboardTabs from '../components/leaderboards/LeaderboardTabs'
import LeaderboardFilters from '../components/leaderboards/LeaderboardFilters'
import leaderboardService from '../services/leaderboardService'
import { useAuth } from '../hooks/useAuth'
import Footer from '../components/organisms/Footer'

const LeaderboardPage = () => {
  const navigate = useNavigate()
  const { isAuthenticated } = useAuth()
  const [searchParams, setSearchParams] = useSearchParams()
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'funding')
  const [timeframe, setTimeframe] = useState(searchParams.get('timeframe') || 'all')
  const [showFilters, setShowFilters] = useState(false)
  const [showAlgorithmInfo, setShowAlgorithmInfo] = useState(false)

  // Update URL when filters change
  useEffect(() => {
    const params = new URLSearchParams()
    params.set('tab', activeTab)
    if (timeframe !== 'all') params.set('timeframe', timeframe)
    setSearchParams(params, { replace: true })
  }, [activeTab, timeframe, setSearchParams])

  // Fetch leaderboard data based on active tab
  const {
    data: leaderboardData,
    isLoading,
    isError,
    error,
    refetch
  } = useQuery({
    queryKey: ['leaderboard', activeTab, timeframe],
    queryFn: () => {
      switch (activeTab) {
        case 'funding':
          return leaderboardService.getTopProjectsByFunding(timeframe, 10)
        case 'supporters':
          return leaderboardService.getTopProjectsBySupporters(timeframe, 10)
        case 'trending':
          return leaderboardService.getTrendingProjects(5)
        case 'success':
          return leaderboardService.getSuccessStories(5)
        default:
          return leaderboardService.getTopProjectsByFunding(timeframe, 10)
      }
    },
    staleTime: 0, // Always fetch fresh data
    cacheTime: 5 * 60 * 1000, // 5 minutes cache time
    retry: 2,
    refetchOnWindowFocus: false
  })

  const handleTabChange = (tab) => {
    setActiveTab(tab)
  }

  const handleTimeframeChange = (newTimeframe) => {
    setTimeframe(newTimeframe)
  }

  const handleRetry = () => {
    refetch()
  }

  const handleSignIn = () => {
    navigate('/login')
  }

  // Tab configuration
  const tabs = [
    {
      id: 'funding',
      label: 'Top by Funding',
      icon: Trophy,
      description: 'Projects that have raised the most funding',
      showTimeframe: true
    },
    {
      id: 'supporters',
      label: 'Top by Supporters',
      icon: Users,
      description: 'Projects with the most supporters',
      showTimeframe: true
    },
    {
      id: 'trending',
      label: 'Trending Now',
      icon: TrendingUp,
      description: 'Projects gaining momentum this week',
      showTimeframe: false
    },
    {
      id: 'success',
      label: 'Success Stories',
      icon: Award,
      description: 'Recently completed projects that met their goals',
      showTimeframe: false
    }
  ]

  const currentTab = tabs.find(tab => tab.id === activeTab)

  // Breadcrumb items
  const breadcrumbItems = [
    { label: 'Leaderboards', href: '/leaderboards' }
  ]

  if (currentTab) {
    breadcrumbItems.push({ label: currentTab.label })
  }

  // Loading skeleton
  const LoadingSkeleton = () => (
    <div className="space-y-4">
      {Array.from({ length: 5 }).map((_, index) => (
        <Card key={index} className="animate-pulse">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <Skeleton className="w-12 h-12 rounded-xl" />
              <Skeleton className="w-16 h-16 rounded-lg" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-5 w-3/4" />
                <Skeleton className="h-4 w-1/2" />
              </div>
              <div className="text-right space-y-2">
                <Skeleton className="h-5 w-20 ml-auto" />
                <Skeleton className="h-4 w-16 ml-auto" />
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  )

  // Error state
  const ErrorState = () => (
    <Card className="text-center py-12">
      <CardContent>
        <div className="text-neutral-400 mb-4">
          <TrendingUp className="w-16 h-16 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-neutral-900 mb-2">
            Unable to load leaderboard
          </h3>
          <p className="text-neutral-600 mb-4">
            {error?.message || 'Something went wrong while fetching the leaderboard data.'}
          </p>
          <Button onClick={handleRetry} variant="outline">
            Try Again
          </Button>
        </div>
      </CardContent>
    </Card>
  )

  // Empty state
  const EmptyState = () => (
    <Card className="text-center py-12">
      <CardContent>
        <div className="text-neutral-400 mb-4">
          <Trophy className="w-16 h-16 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-neutral-900 mb-2">
            No projects found
          </h3>
          <p className="text-neutral-600">
            {activeTab === 'trending'
              ? 'No trending projects at the moment. Check back later!'
              : 'No projects match the current criteria.'}
          </p>
        </div>
      </CardContent>
    </Card>
  )

  // Trending Algorithm Explanation
  const AlgorithmExplanation = () => (
    <motion.div
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: 'auto' }}
      exit={{ opacity: 0, height: 0 }}
      transition={{ duration: 0.3 }}
      className="mb-6"
    >
      <Card className="bg-gradient-to-r from-orange-50 to-red-50 border-orange-200">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 bg-gradient-to-br from-orange-500 to-red-500 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
              <Info className="w-4 h-4 text-white" />
            </div>
            <div>
              <h4 className="font-semibold text-orange-900 mb-2">How Trending Works</h4>
              <p className="text-sm text-orange-800 mb-3">
                Our trending algorithm identifies projects gaining momentum over the past 7 days by analyzing:
              </p>
              <ul className="text-sm text-orange-700 space-y-1">
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-orange-500 rounded-full"></span>
                  <strong>Recent donations</strong> - More donations = higher trending score
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-orange-500 rounded-full"></span>
                  <strong>New supporters</strong> - Diverse backers are weighted heavily
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-orange-500 rounded-full"></span>
                  <strong>Recent funding</strong> - Total amount raised this week
                </li>
              </ul>
              <p className="text-xs text-orange-600 mt-3 italic">
                Updated every 15 minutes to show the most current trending projects
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  )

  return (
    <div className="min-h-screen bg-neutral-50">

      {/* Main Content */}
      <div className="min-h-screen bg-white">
        {/* Page Header with Breadcrumbs */}
        <div className="bg-neutral-50 border-b border-neutral-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Breadcrumb items={breadcrumbItems} className="mb-4" />
              <div className="flex items-center gap-2 sm:gap-3 mb-3">
                <Trophy className="w-5 h-5 sm:w-6 sm:h-6 text-primary-teal" />
                <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-neutral-900">
                  Project Leaderboards
                </h1>
              </div>
              <p className="text-sm sm:text-base text-neutral-600">
                Discover the most successful projects on CROWBLOCK
              </p>
            </motion.div>
          </div>
        </div>

        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* Mobile-optimized Tab Navigation */}
          <div className="mb-4 sm:mb-6 overflow-x-auto pb-2">
            <LeaderboardTabs
              tabs={tabs}
              activeTab={activeTab}
              onTabChange={handleTabChange}
            />
          </div>

          {/* Trending Algorithm Explanation */}
          {activeTab === 'trending' && (
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold text-neutral-900">Understanding Trending</h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowAlgorithmInfo(!showAlgorithmInfo)}
                  className="text-orange-600 hover:text-orange-700"
                >
                  <Info className="w-4 h-4 mr-1" />
                  {showAlgorithmInfo ? 'Hide' : 'Show'} Algorithm
                </Button>
              </div>
              
              {showAlgorithmInfo && <AlgorithmExplanation />}
            </div>
          )}

          {/* Mobile-friendly Filters */}
          <div className="mb-4 sm:mb-6">
            <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
              <div className="flex items-center gap-2 sm:gap-3">
                <h2 className="text-base sm:text-lg font-semibold text-neutral-900">
                  {currentTab?.label}
                </h2>
                <Badge variant="secondary" className="bg-neutral-100 text-neutral-700 text-xs">
                  {leaderboardData?.leaderboard?.length || 0}
                </Badge>
              </div>

              {currentTab?.showTimeframe && (
                <div className="mt-2 sm:mt-0">
                  <LeaderboardFilters
                    timeframe={timeframe}
                    onTimeframeChange={handleTimeframeChange}
                    showFilters={showFilters}
                    onToggleFilters={() => setShowFilters(!showFilters)}
                  />
                </div>
              )}
            </div>
            
            {/* Tab description */}
            <p className="text-sm text-neutral-600 mt-2">
              {currentTab?.description}
            </p>
          </div>

          {/* Leaderboard Content */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            {isLoading ? (
              <LoadingSkeleton />
            ) : isError ? (
              <ErrorState />
            ) : !leaderboardData?.leaderboard?.length ? (
              <EmptyState />
            ) : (
              <div className="w-full space-y-3 sm:space-y-4">
                {leaderboardData.leaderboard.map((project, index) => (
                  <div key={project.id} className="w-full">
                    <ProjectRankingCard
                      project={project}
                      type={activeTab}
                      index={index}
                    />
                  </div>
                ))}
              </div>
            )}

            {/* Fallback Notice */}
            {leaderboardData?.fallback && (
              <Card className="mt-4 sm:mt-6 border-yellow-200 bg-yellow-50">
                <CardContent className="p-3 sm:p-4">
                  <div className="flex items-center gap-2 text-yellow-800">
                    <Filter className="w-4 h-4 sm:w-5 sm:h-5" />
                    <span className="text-xs sm:text-sm">
                      Showing cached data. Live data will be available shortly.
                    </span>
                  </div>
                </CardContent>
              </Card>
            )}
          </motion.div>
        </div>
      </div>

      <Footer variant="full" />
    </div>
  )
}

export default LeaderboardPage